import * as z from "zod";

// *** NOTE: importing from @shared/utils is not working ***
// *** so we are importing with relative path the functions here ***
// It create a circular dependency
// import { dateIsFuture, getAsNumber, isEmpty } from "@shared/utils";
import { dateIsFuture } from "../../utils/dates";
import { getAsNumber } from "../../utils/get-as-number";
import { isEmpty } from "../../utils/is-empty";
import { amountSchema, optionalNumber } from "../generic";

import { addressSchema } from "./address.validation";
import { AmountFrequencyEnum } from "./applicant.enum";

export const optionalAmount = z.preprocess(
  (v) => (isEmpty(v) ? null : getAsNumber(v)),
  z.number().nullable().optional()
);

export const rentalIncomeAmountFrequency = z.object({
  amount: z.preprocess(
    (v) => (isEmpty(v) ? null : getAsNumber(v)),
    z
      .number({
        error: () => "form:error.missingAmount",
        coerce: false,
      })
      .max(9_999_999, {
        message: "form:error.maxDollarAmount",
      })
      .min(1, { message: "form:error.missingAmount" })
  ),
  frequency: z.enum(AmountFrequencyEnum, {
    error: () => "form:error.missingFrequency",
  }),
});

// duplicate name to avoid breaking changes
export const rentalIncomeAmountFrequencyOptional = z.object({
  amount: z
    .preprocess(
      (v) => (isEmpty(v) ? null : getAsNumber(v)),
        z
          .number({
            error: (issue) =>
              issue.input === undefined ? "form:error.required" : "type",
            coerce: false,
          })
          .nullable()
    )
    .optional(),
  frequency: z.preprocess(
    (v) => (isEmpty(v) ? "" : v),
    z
      .enum([
        "WEEKLY",
        "BIWEEKLY",
        "MONTHLY",
        "SEMIMONTHLY",
        "SEMIANNUALLY",
        "ANNUALLY",
        "",
      ])
      .nullable()
      .optional()
  ),
});

export const condoFeesSchema = z
  .object({
    amount: z
      .preprocess(
        (v) => (isEmpty(v) ? null : getAsNumber(v)),
        z
          .number({
            error: () => "form:error.required",
            coerce: false,
          })
          .max(9_999_999, "form:error.maxDollarAmount")
      )
      .optional(),
    frequency: z
      .enum([
        "ANNUALLY",
        "SEMIANNUALLY",
        "MONTHLY",
        "SEMIMONTHLY",
        "BIWEEKLY",
        "WEEKLY",
        "",
      ])
      .optional(),
    heatingIncluded: z.boolean().nullable().optional(),
  })
  // make sure if one is filled, the other is filled
  .refine(
    (schema) => {
      // frequency is not empty and amount is null / empty field
      if (!isEmpty(schema.frequency) && isEmpty(schema.amount)) {
        return false;
      }

      return true;
    },
    {
      message: "form:error.required",
      path: ["amount"],
    }
  )
  .refine(
    (schema) => {
      // we want to support negative values for the amount and just check it is not 0
      return schema.amount && schema.amount !== 0 ? !!schema.frequency : true;
    },
    {
      message: "form:error.required",
      path: ["frequency"],
    }
  )
  .nullable()
  .optional();

export type CondoFeesType = z.infer<typeof condoFeesSchema>;

export const baseSchema = z.object({
  id: z.number().optional(),
  address: addressSchema,
  currentPurpose: z.enum(
    [
      "OWNER_OCCUPIED",
      "RENTAL",
      "OWNER_OCCUPIED_AND_RENTAL",
      "SECONDARY_RESIDENCE",
      "VACANT",
      "SeasonalHome",
      "RECREATIONAL_PROPERTY",
      "RESERVE_PROPERTY",
      "COTTAGE_PROPERTY",
      "OTHER",
    ],
    {
      error: () => "form:error.required",
    }
  ),
  afterTransactionPurpose: z.enum(
    [
      "OWNER_OCCUPIED",
      "RENTAL",
      "OWNER_OCCUPIED_AND_RENTAL",
      "SOLD",
      "SECONDARY_RESIDENCE",
      "VACANT",
      "SeasonalHome",
      "RECREATIONAL_PROPERTY",
      "RESERVE_PROPERTY",
      "COTTAGE_PROPERTY",
      "OTHER",
    ],
    {
      error: () => "form:error.required",
    }
  ),
  specifiedAfterTransactionPurpose: z.string().trim().optional().nullable(),
  type: z.string().trim().optional().nullable(),
  estimatedPropertyValue: optionalNumber,
  taxes: z.object({ amount: optionalAmount, year: optionalNumber }).optional(),
  hasMortgage: z.boolean().optional().nullable(),
  rentalIncome: rentalIncomeAmountFrequencyOptional.nullable().optional(),
  condoFees: condoFeesSchema,
  heatingCost: z
    .object({
      amount: optionalAmount,
      frequency: z.string().trim().optional().nullable(),
    })
    .optional()
    .nullable(),
  currentSaleStatus: z.string().trim().optional().nullable(),
  sellingDate: z.string().trim().optional().nullable(),
  purchasePrice: optionalNumber,
  financialInstitution: z.string().trim().optional().nullable(),
});

export const mortgageSchema = z.object({
  mortgage: z.object({
    balance: amountSchema,
    interestRate: z
      .number({
        error: () => "form:error.required",
      })
      .max(99.99, "form:error.maxInterest")
      .refine(
        (value) => {
          return value !== null && value > 0;
        },
        {
          message: "form:error.required",
        }
      ),
    interestRateType: z.enum(
      ["FIXED", "VARIABLE", "ADJUSTABLE", "CAPPED_VARIABLE"],
      {
        error: () => "form:error.required",
      }
    ),
    lender: z.string().trim().min(1, { message: "form:error.required" }),
    maturityDate: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, { message: "form:error.required" }),
    mortgageType: z.enum(["STANDARD", "HELOC"], {
      error: () => "form:error.required",
    }),
    payment: z.object({
      amount: amountSchema,
      frequency: z.enum(
        [
          "WEEKLY",
          "ACCELERATED_WEEKLY",
          "BIWEEKLY",
          "ACCELERATED_BIWEEKLY",
          "SEMIMONTHLY",
          "MONTHLY",
        ],
        {
          error: () => "form:error.required",
        }
      ),
    }),
    termType: z.enum(["OPEN", "CLOSED", "CONVERTABLE"], {
      error: () => "form:error.required",
    }),
    existingLoanNumber: z.string().trim().optional().nullable(),
  }),
});

export type Mortgage = z.infer<typeof mortgageSchema>;

export const optionalMortgage = z.object({
  mortgage: z
    .object({
      balance: optionalAmount,
      interestRate: optionalNumber,
      interestRateType: z.string().trim().optional(),
      lender: z.string().trim().optional(),
      maturityDate: z.string().trim().optional(),
      mortgageType: z.string().trim().optional(),
      payment: z
        .object({
          amount: optionalAmount,
          frequency: z
            .enum([
              "WEEKLY",
              "ACCELERATED_WEEKLY",
              "BIWEEKLY",
              "ACCELERATED_BIWEEKLY",
              "SEMIMONTHLY",
              "MONTHLY",
              "",
            ])
            .nullable()
            .optional(),
        })
        .optional()
        .nullable(),
      termType: z.string().trim().optional(),
      existingLoanNumber: z.string().trim().optional().nullable(),
    })
    .partial()
    .optional(),
});

export type OptionalMortgage = z.infer<typeof optionalMortgage>;

export const mortgageSchemaUnion = z.discriminatedUnion("hasMortgage", [
  mortgageSchema.extend({
    hasMortgage: z.literal(true),
  }),
  z.object({
    hasMortgage: z.literal(false),
  }),
]);

export const mortgageSchemaUnionWithFinancialInstitution = mortgageSchemaUnion.and(
  z.object({
    financialInstitution: z
      .string({ error: () => "form:error.required" })
      .trim()
      .min(1, { message: "form:error.required" }),
  })
);

export const refinedMortgageSchema = z
  .object({
    hasMortgage: z.boolean().optional().nullable(),
  })
  .and(optionalMortgage)
  .superRefine((values, ctx) => {
    if (values.hasMortgage) {
      const isOk = mortgageSchema.safeParse(values);

      if (!isOk.success) {
        isOk.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "form:error.required",
            path: [...issue.path],
          });
        });
      }
    }
  });

export const rentalIncomeSchema = baseSchema
  .pick({
    afterTransactionPurpose: true,
    rentalIncome: true,
  })
  .superRefine((values, ctx) => {
    if (
      ["RENTAL", "OWNER_OCCUPIED_AND_RENTAL"].includes(
        values.afterTransactionPurpose
      )
    ) {
      const isOk = rentalIncomeAmountFrequency.safeParse(values.rentalIncome);

      if (!isOk.success) {
        isOk.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ["rentalIncome", ...issue.path],
          });
        });
      }
    }
  });

export const soldSchema = baseSchema
  .pick({
    currentPurpose: true,
    afterTransactionPurpose: true,
  })
  .extend({
    currentSaleStatus: z.enum(
      ["NOT_READY_YET", "LISTED_FOR_SALE", "CONDITIONALLY_SOLD", "SOLD_FIRM"],
      {
        error: () => "form:error.required",
      }
    ),
    sellingDate: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .refine(dateIsFuture, {
        message: "form:error.dateShouldBeFuture",
      }),
    purchasePrice: z
      .number({
        error: () => "form:error.required",
      })
      .max(9_999_999, "form:error.maxDollarAmount")
      .refine((value) => value > 0, {
        message: "form:error.required",
      }),
  });

export const heatingIncludedSchema = baseSchema
  .pick({
    condoFees: true,
  })
  .extend({
    type: z.enum(
      [
        "Condominium",
        "HOUSE",
        "SECONDARY_HOME_OR_COTTAGE",
        "DUPLEX",
        "TRIPLEX",
        "FOURPLEX",
        "DETACHED",
        "SEMI_DETACHED",
        "DUPLEX_DETACHED",
        "DUPLEX_SEMI_DETACHED",
        "ROW_HOUSING",
        "APARTMENT_LOW_RISE",
        "APARTMENT_HIGH_RISE",
        "MOBILE",
        "TRI_PLEX_DETACHED",
        "TRI_PLEX_SEMI_DETACHED",
        "STACKED",
        "MODULAR_HOME_DETACHED",
        "MODULAR_HOME_SEMI_DETACHED",
        "FOUR_PLEX_DETACHED",
        "FOUR_PLEX_SEMI_DETACHED",
        "SeasonalHome",
        "OTHER",
      ],
      {
        error: () => "form:error.required",
      }
    ),
  })
  .superRefine((values, ctx) => {
    if (values.type === "Condominium") {
      const isOk = z.boolean().safeParse(values.condoFees?.heatingIncluded);

      if (!isOk.success) {
        isOk.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "form:error.required",
            path: ["condoFees.heatingIncluded", ...issue.path],
          });
        });
      }
    }
  });

export const propertyPurposeSchema = baseSchema
  .pick({
    currentPurpose: true,
  })
  .extend({
    estimatedPropertyValue: amountSchema,
    taxes: z.object({
      amount: amountSchema,
      year: z.number({
        error: () => "form:error.required",
      }),
    }),
  })
  .and(rentalIncomeSchema)
  .and(heatingIncludedSchema);

export const hasMortgageSchema = baseSchema.superRefine((values, ctx) => {
  if (values.afterTransactionPurpose !== "SOLD") {
    const isOk = z.boolean().safeParse(values.hasMortgage);

    if (!isOk.success) {
      isOk.error.issues.forEach((issue) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "form:error.required",
          path: ["hasMortgage", ...issue.path],
        });
      });
    }
  }
});

export const schema = hasMortgageSchema.and(refinedMortgageSchema);

export const soldMortgageSchema = schema.and(soldSchema);

export type OwnedPropertyFormData = z.infer<typeof schema>;
